import type { TabData } from "@wayflyer/flyui";
import { AppShell, FullWidthPage, Callout, Tabs } from "@wayflyer/flyui";
import { useIntl } from "react-intl";

import { DataGroupContainer, Spinner } from "../../../../components/styled";
import type { ConsumersChurnTabProps } from "../consumers-churn-tab";
import { ConsumersChurnTab } from "../consumers-churn-tab";
import type { ConsumersHeaderRowProps } from "../consumers-header-row";
import { ConsumersHeaderRow } from "../consumers-header-row";
import type { ConsumersLtvTabProps } from "../consumers-ltv-tab";
import { ConsumersLtvTab } from "../consumers-ltv-tab";
import type { ConsumersSummaryTabProps } from "../consumers-summary-tab";
import { ConsumersSummaryTab } from "../consumers-summary-tab";

export interface ConsumersScreenProps {
  pageTitle: string;
  dataLoading?: boolean;
  dataError?: string;
  selectedTabIndex?: number;
  onSelectTab?: (title: string, tabIndex: number) => void;
  summaryTabProps?: ConsumersSummaryTabProps;
  ltvTabProps?: ConsumersLtvTabProps;
  churnTabProps?: ConsumersChurnTabProps;
  headerRowProps?: ConsumersHeaderRowProps;
}

/**
 * ConsumersScreen component
 */
export const ConsumersScreen = ({
  pageTitle,
  ltvTabProps,
  headerRowProps,
  churnTabProps,
  summaryTabProps,
  dataLoading = false,
  dataError = "",
  selectedTabIndex = 0,
  onSelectTab = () => {},
}: ConsumersScreenProps): UIElement => {
  const intl = useIntl();

  const tabs: TabData[] = [];

  if (summaryTabProps) {
    tabs.push({
      title: intl.formatMessage({ id: "Consumers Screen: LTV Tab Title", defaultMessage: "LTV Chart" }),
      content: <ConsumersSummaryTab {...summaryTabProps} />,
      disabled: false,
    });
  }

  if (ltvTabProps) {
    tabs.push({
      title: intl.formatMessage({ id: "Consumers Screen: LTV-CAC Tab Title", defaultMessage: "LTV-CAC" }),
      content: <ConsumersLtvTab {...ltvTabProps} />,
      disabled: false,
    });
  }

  if (churnTabProps) {
    tabs.push({
      title: intl.formatMessage({ id: "Consumers Screen: Customer Churn Tab Title", defaultMessage: "Customer Churn" }),
      content: <ConsumersChurnTab {...churnTabProps} />,
      disabled: false,
    });
  }

  return (
    <AppShell>
      <FullWidthPage title={pageTitle}>
        {dataError ? <Callout variant="critical" description={dataError} /> : null}
        {dataLoading ? <Spinner /> : null}
        {!dataError && !dataLoading ? (
          <>
            {headerRowProps ? <ConsumersHeaderRow {...headerRowProps} /> : null}
            <DataGroupContainer>
              <Tabs tabs={tabs} selectedTabIndex={selectedTabIndex} onSelectTab={onSelectTab} />
            </DataGroupContainer>
          </>
        ) : null}
      </FullWidthPage>
    </AppShell>
  );
};
